import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
// "/pure" – Stripe.js sa vloží do stránky až pri zavolaní loadStripe(),
// nie hneď pri importe (hlavný vstup ho vkladá ako side effect na každej stránke).
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  CANCEL_PAYMENT_INTENT_URL,
  CREATE_PAYMENT_INTENT_SUBSCRIPTION_URL,
  CREATE_SUBSCRIPTION_INTENT_URL,
  VERIFY_PAYMENT_URL,
} from "../../features/UrlHelper";
import "./StripePaymentModal.scss";
import { handleApiError } from "../../features/errorHandler";
import { useAuth } from "../../context/AuthContext";
import { pushGTMEvent } from "../../analytics/gtm";

let stripePromise = null;

// Stripe.js sa načíta až pri prvom otvorení platobného okna
function getStripe() {
  if (!stripePromise) {
    stripePromise = loadStripe(window.location.hostname !== "capoeira.sk" ? process.env.REACT_APP_STRIPE_PUBLIC_KEY : process.env.REACT_APP_STRIPE_PUBLIC_KEY_PROD);
  }
  return stripePromise;
}

function formatCurrency(amount, currency) {
  switch (currency?.toUpperCase()) {
    case "EUR":
    case "€":
      return "€";
    case "USD":
    case "$":
      return "$";
    case "GBP":
    case "£":
      return "£";
    default:
      return "€";
  }
}

/**
 * 1. Komponent formulára, ktorý komunikuje priamo so Stripe
 */
function CheckoutForm({
  amount,
  onClose,
  onSuccess,
  currency = "EUR",
  isSetupIntent = false,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage(null);

    if (isSetupIntent) {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || "Pri spracovaní platby nastala chyba.",
        );
        setLoading(false);
      } else if (setupIntent && setupIntent.status === "succeeded") {
        setLoading(false);
        onSuccess();
      }
    } else {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || "Pri spracovaní platby nastala chyba.",
        );
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === "processing") {
        setLoading(false);
        setErrorMessage(
          "Platba sa práve spracováva. Po jej dokončení dostanete e-mailové potvrdenie.",
        );
      } else if (!paymentIntent || paymentIntent.status !== "succeeded") {
        setLoading(false);
        setErrorMessage("Platbu sa nepodarilo dokončiť. Skúste to prosím znova.");
      } else {
        // Platba v Stripe prebehla — teraz overíme zápis v DB
        try {
          const verifyRes = await axios.post(
            VERIFY_PAYMENT_URL,
            { paymentIntentId: paymentIntent.id },
            {
              headers: {
                Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
              },
            },
          );

          if (verifyRes.data?.verified) {
            setLoading(false);
            onSuccess(paymentIntent.id);
          } else if (verifyRes.data?.pending) {
            setLoading(false);
            setErrorMessage(
              verifyRes.data?.message ||
              "Platba sa práve spracováva. Po jej dokončení dostanete e-mailové potvrdenie.",
            );
          } else {
            setLoading(false);
            setErrorMessage(
              verifyRes.data?.message ||
              "Platba bola úspešná v Stripe, ale zlyhal jej zápis do databázy. Vytváranie nových platieb bolo zablokované.",
            );
          }
        } catch (verifyErr) {
          setLoading(false);
          const msg = handleApiError(
            verifyErr,
            `Platba bola úspešná v Stripe, ale zápis do databázy zlyhal. Transakcia ID: ${paymentIntent.id}`
          );
          setErrorMessage(msg);
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-modal__form">
      <PaymentElement
        options={{
          wallets: {
            applePay: "auto",
            googlePay: "auto",
            link: "never",
          },
        }}
      />

      {errorMessage && (
        <div
          className="stripe-modal__error"
          style={{
            color: "#dc3545",
            marginTop: "15px",
            padding: "10px",
            background: "#fff5f5",
            border: "1px solid #ffc9c9",
            borderRadius: "6px",
            fontSize: "0.9rem",
            lineHeight: "1.4",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div
        className="stripe-modal__footer"
        style={{
          marginTop: "25px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
        }}
      >
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={loading}
        >
          Zrušiť
        </Button>
        <Button type="submit" variant="primary" disabled={loading || !stripe}>
          {loading ? (
            <span className="stripe-modal__spinner" aria-hidden="true" />
          ) : (
            `Zaplatiť ${amount ? `${amount.toFixed(2)} ${formatCurrency(currency)}` : ""}`
          )}
        </Button>
      </div>
    </form>
  );
}

/**
 * 2. Hlavný Modal komponent (Wrapper)
 */
function StripePaymentModal({
  isOpen,
  onClose,
  amount,
  productName,
  userEmail,
  currency = "EUR",
  title = "Bezpečná platba",
  productId = 0,
  duration = null,
  repeatChecked = false,
  loadUserSubscriptions
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [intentType, setIntentType] = useState(null); // "setup" alebo "payment"
  const [isSuccess, setIsSuccess] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [initError, setInitError] = useState(null);
  const [initErrorType, setInitErrorType] = useState(null);
  // Suma, ktorú vypočítal server (cenník + zľava z DB) – tá sa aj strhne
  const [serverAmount, setServerAmount] = useState(null);
  const { userId } = useAuth();
  const displayAmount = serverAmount ?? amount;
  const requestInFlight = useRef(false); // Ochrana pred duplicitným volaním

  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setIntentType(null);
      setInitError(null);
      setInitErrorType(null);
      setServerAmount(null);
      return;
    }

    // Ochrana: ak už request beží alebo máme secret, nerob nič
    if (requestInFlight.current || clientSecret) return;

    const abortController = new AbortController();
    requestInFlight.current = true;
    setIsSuccess(false);
    setInitError(null);
    setInitErrorType(null);

    const url = repeatChecked
      ? CREATE_PAYMENT_INTENT_SUBSCRIPTION_URL
      : CREATE_SUBSCRIPTION_INTENT_URL;

    // Jednorazová platba: cenu, názov a platnosť určuje server podľa plánu
    const payload = repeatChecked
      ? {
        amount,
        productName,
        userEmail,
        currency,
        productId,
        duration,
        userId,
      }
      : { productId };

    axios
      .post(url, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
        signal: abortController.signal,
      })
      .then((res) => {
        if (!abortController.signal.aborted) {
          setClientSecret(res.data.clientSecret);
          setIntentType(res.data.type || "payment");
          if (typeof res.data.amount === "number") {
            setServerAmount(res.data.amount);
          }
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          const serverMsg = handleApiError(err, "Nepodarilo sa vytvoriť platobný zámer.");
          const errorType = err.response?.data?.type;
          setInitErrorType(errorType || null);
          setInitError(serverMsg);
        }
      })
      .finally(() => {
        requestInFlight.current = false;
      });

    return () => {
      abortController.abort();
      requestInFlight.current = false;
    };
  }, [isOpen]);

  // Zatvorenie bez zaplatenia: neukončený PaymentIntent hneď zrušíme, aby v Stripe neostal
  // ako Incomplete. Backend ho zruší, len ak ešte nie je zaplatený ani spracovávaný.
  const handleClose = () => {
    const paymentIntentId = clientSecret?.startsWith("pi_")
      ? clientSecret.split("_secret_")[0]
      : null;
    if (paymentIntentId && !isSuccess) {
      axios
        .post(
          CANCEL_PAYMENT_INTENT_URL,
          { paymentIntentId },
          {
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
            },
          },
        )
        .catch((err) =>
          handleApiError(err, "Nepodarilo sa zrušiť neukončenú platbu."),
        );
    }
    // Po úspešnej platbe obnovíme predplatné aj pri zavretí krížikom, aby sa nedalo kúpiť znova
    if (isSuccess) {
      loadUserSubscriptions?.();
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="stripe-modal"
    >
      <div className="stripe-modal__body" style={{ padding: "20px" }}>
        {isSuccess ? (
          <div
            style={{ textAlign: "center", color: "#28a745", padding: "20px 0" }}
          >
            <h3 style={{ marginBottom: "10px" }}>Platba prebehla úspešne!</h3>
            <p>Ďakujeme, na e-mail sme vám zaslali potvrdenie.</p>
            {paymentIntentId && (
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "0.8rem",
                  color: "#6c757d",
                }}
              >
                Číslo transakcie:{" "}
                <code
                  style={{
                    background: "#f1f3f5",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                  }}
                >
                  {paymentIntentId}
                </code>
                <br />
                <span style={{ fontSize: "0.75rem", color: "#adb5bd" }}>
                  Uložte si ho pre prípadnú reklamáciu.
                </span>
              </p>
            )}
            <Button
              onClick={() => {
                onClose();
                loadUserSubscriptions();
              }}
              style={{ marginTop: "20px" }}
            >
              Zatvoriť
            </Button>
          </div>
        ) : (
          <>
            <div
              style={{
                background: "#f8f9fa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{productName}</span>
                <strong>
                  {displayAmount
                    ? `${displayAmount.toFixed(2)} ${formatCurrency(currency)}`
                    : ""}
                </strong>
              </div>
            </div>

            {clientSecret ? (
              <Elements
                stripe={getStripe()}
                options={{ clientSecret, appearance: { theme: "stripe" }, locale: "sk" }}
              >
                <CheckoutForm
                  amount={displayAmount}
                  onClose={handleClose}
                  onSuccess={(piId) => {
                    setPaymentIntentId(piId || null);
                    setIsSuccess(true);
                    pushGTMEvent("purchase", {
                      transaction_id: piId,
                      value: displayAmount,
                      currency: currency || "EUR",
                      items: [{ item_name: productName, price: displayAmount, quantity: 1 }],
                    });
                  }}
                  currency={currency}
                  isSetupIntent={intentType === "setup"}
                />
              </Elements>
            ) : initError ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: initErrorType === "PROCESSING" ? "#856404" : "#dc3545",
                  background: initErrorType === "PROCESSING" ? "#fff3cd" : "#fff5f5",
                  borderRadius: "8px",
                  border: `1px solid ${initErrorType === "PROCESSING" ? "#ffeeba" : "#ffc9c9"}`,
                }}
              >
                <h4 style={{ marginBottom: "10px" }}>
                  {initErrorType === "PROCESSING"
                    ? "⏳ Platba sa spracováva"
                    : initErrorType === "ADDRESS_MISSING"
                      ? "Chýba adresa"
                      : initErrorType === "PAID_MEANWHILE"
                        ? "✅ Platba už prebehla"
                        : "🛑 Platba bola zablokovaná"}
                </h4>
                <p style={{ fontSize: "0.95rem", lineHeight: "1.5" }}>{initError}</p>
                {initErrorType === "ADDRESS_MISSING" && (
                  <p style={{ marginTop: "10px" }}>
                    <Link to="/profil" onClick={onClose}>
                      Doplniť adresu v profile
                    </Link>
                  </p>
                )}
                <Button onClick={handleClose} style={{ marginTop: "20px" }}>
                  Zatvoriť
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Načítavam bezpečnú platbu...
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default StripePaymentModal;
